import { useCallback, useEffect, useRef, useState } from "react"
import LivePreview from "../LivePreview"
import PricingModal from "../PricingModal"
import { WHICH_FILE_HINT } from "../HeroResult"
import MobileProSheet from "./MobileProSheet"
import MobileSaveSheet from "./MobileSaveSheet"
import MobileSettingPanels, { CATEGORIES, summarize } from "./MobileSettingPanels"
import useChangeEcho from "../../hooks/useChangeEcho"

// The mobile live state: the code sits on a stage that never scrolls away, and
// the settings live in a sheet the user drags between four heights.
//
// The problem this replaces: on a phone the desktop workspace stacks the
// customise controls *below* the result, so the code scrolls out of view at
// exactly the moment you are changing it. Splitting the screen by one number —
// the sheet's share of the height — means the code is always on screen and the
// user chooses how much of it to trade for controls.
//
// Rendered as a fixed overlay rather than in the page flow. The builder is an
// island inside a scrolling marketing page, so "never scrolls away" is only
// true if the page can't scroll underneath it. The design assumed a standalone
// phone screen and never had to answer what "leaving" means here — the answer
// is that you don't: on a phone this IS the hero. The ways out are collapsing
// the drawer (see the code at full size) and starting a new code (back to the
// idle hero and the scrolling page), both of which keep the code alive.
//
// Design handoff: ~/Downloads/design_handoff_mobile_customizer (Direction B).

// Four rungs, not the handoff's three. The lowest is a *closed* drawer: grabber
// and footer only, code at full size. The handoff didn't need one because its
// prototype was a standalone screen you could never be stuck on; here the shell
// IS the hero, so "get this panel out of my way" has to be reachable without
// leaving the code behind.
const SNAPS = [ 0.18, 0.34, 0.62, 0.90 ]
const CLOSED_RUNG = 0
const PEEK_RUNG = 1
const HALF_RUNG = 2
const MAX_FRAC = 0.92
// The closed drawer's "Style your code" row plus the sheet's 1px top border. The
// grabber and footer are measured: the grabber is sized in rem, and the
// safe-area inset is part of the footer's height.
const CLOSED_ROW_PX = 44 + 1
// Drawer closed: nothing between the grabber and the footer. Counted from the
// lowest rung, because that rung moves on a short screen.
const COLLAPSED_BAND = 0.06
// The least room between two rungs. Wider than the band above, so the rung over
// the closed one always opens the drawer.
const RUNG_GAP = 0.08
// Below this the sheet shows the 2x2 summary grid instead of a working panel.
const PEEK_MAX = 0.45
// Pointer travel under which a drag counts as a tap. Enough to absorb the
// wobble of a thumb pressing a 44px target, short enough that a deliberate
// nudge still resizes.
const TAP_SLOP_PX = 6

// 0.18 of the height is 90px on a 320x568 phone, and the closed drawer's content
// needs about 170px, which pushed Download and Save code off the screen. So the
// lowest rung is never shorter than its content, and each rung above it moves up
// to stay a separate stop. On the 844px phone the handoff drew, only the lowest
// rung moves.
function rungsFor(floorFrac) {
  const rungs = []
  SNAPS.forEach((snap, i) => {
    const lowest = i === 0 ? floorFrac : rungs[i - 1] + RUNG_GAP
    rungs.push(Math.min(MAX_FRAC, Math.max(snap, lowest)))
  })
  return rungs
}

function nearestRung(rungs, frac) {
  return rungs.reduce((best, rung, i) => (Math.abs(rung - frac) < Math.abs(rungs[best] - frac) ? i : best), 0)
}

function useMeasuredHeight(initialPx, read = (el) => el.offsetHeight) {
  const ref = useRef(null)
  const [ px, setPx ] = useState(initialPx)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const measure = () => setPx(read(el))
    measure()
    if (typeof ResizeObserver === "undefined") return
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return [ ref, px ]
}

// The QR shrinks as the sheet grows, so the code stays visible instead of being
// swallowed by the sheet's top edge.
const QR_BASE_PX = 188
// The stage's fixed padding and the gap above the chrome. Everything else
// below the code — the link pill, the saved note, a restyle error — is
// measured, because its height changes with content.
const STAGE_PADDING_PX = 14 + 10 + 10
// Below this the code is too small to be worth showing at all; the stage stops
// shrinking it and lets the sheet cover the overflow instead.
const QR_MIN_PX = 96
// The closed drawer's whole purpose is seeing the code, so it may grow past the
// handoff's 188px into the room it just gained. The design's own three snaps
// keep the specified size — this ceiling only applies to the rung that isn't
// theirs. Capped so it still breathes inside a 360px screen's gutters.
const QR_MAX_COLLAPSED_PX = 260

// The handoff specifies three fixed scale steps (0.62 / 0.82 / 1) keyed off the
// sheet fraction. Those numbers are right on the 844px phone they were drawn
// on and wrong on a shorter one: on a 375x667 iPhone SE the 0.82 step still
// left the code tall enough to push the link pill 7px underneath the sheet.
//
// Same intent, derived instead of assumed — take what the stage actually has
// and give the code the remainder. On the reference device this lands within a
// few px of the specified steps; on a short screen it stays correct.
function qrSizePx(stageHeightPx, chromePx, collapsed) {
  if (!stageHeightPx) return QR_BASE_PX
  const available = stageHeightPx - STAGE_PADDING_PX - chromePx
  const ceiling = collapsed ? QR_MAX_COLLAPSED_PX : QR_BASE_PX
  return Math.max(QR_MIN_PX, Math.min(ceiling, Math.round(available)))
}

function Icon({ name, size = 16 }) {
  const paths = {
    copy:  <><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></>,
    check: <polyline points="20 6 9 17 4 12" />,
    plus:  <><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>,
    download: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></>,
    mail:  <><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></>,
    lock:  <><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></>,
    sparkles: <path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z" />
  }
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[name]}
    </svg>
  )
}

// 44px, not the 40px the handoff's stage table specifies. That table is at odds
// with the same document's "minimum tap target 44px throughout" and its a11y
// rule ("all targets >= 44px"), and 44 is the number that appears twice and the
// one that matters on a surface driven by a thumb. The pill grows 4px.
const ICON_BTN =
  "inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full " +
  "text-[color:var(--text-secondary)] transition hover:text-[color:var(--text-primary)] " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2"

const DOWNLOAD_BTN =
  "inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-md border border-[color:var(--border-default)] " +
  "text-base font-semibold text-[color:var(--text-primary)] transition hover:border-voltage " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2"

function pngFor(imageUrl) {
  if (!imageUrl) return null
  return imageUrl.replace(/\.svg(\?.*)?$/, ".png$1")
}

// The server's attachment header, not only the anchor's `download` attribute:
// an in-app browser (a link opened from ChatGPT, say) can ignore the attribute,
// and then the image replaces this screen and no file is saved.
function asDownload(imageUrl) {
  const url = new URL(imageUrl, window.location.href)
  url.searchParams.set("download", "1")
  return url.href
}

export default function MobileBuilderSheet({
  result, design, setDesign, restyleError, onReset, onUpgrade,
  isPaid = false, creatorEmail = null, emailStatus = undefined, restored = false, onEmailSaved
}) {
  // "not_saved" means a binding ceiling refused the address: the create still
  // echoes it back, but nothing was persisted against the code, so it is NOT a
  // delivery address and claiming otherwise would be a lie the visitor can only
  // discover by failing to sign in later. HeroResult draws the same distinction.
  const bound = creatorEmail && emailStatus !== "not_saved"
  const [ echoMessage, echo ] = useChangeEcho()
  const [ category, setCategory ] = useState("colors")
  // The rung, not its height: the rungs follow the measured height, which can
  // change at any time, and the sheet must stay on the rung the user chose.
  const [ rung, setRung ] = useState(HALF_RUNG)
  const [ dragFrac, setDragFrac ] = useState(null)
  // Opens on "intro" for an unclaimed code: the email capture comes BEFORE the
  // customiser, exactly as it does on desktop (HeroResult opens its capture
  // modal the moment a code goes live). Routing the mobile shell around
  // HeroResult accidentally dropped that, which quietly turned the strongest
  // moment in the funnel — the code just appeared, and it can be kept — into
  // something the visitor only meets on their way out.
  // Not for a code reopened from the "Your recent code" card (`restored`,
  // issue #264): it did not just appear, so the intro has no moment to land in.
  const [ modal, setModal ] = useState(bound || restored ? null : "leave")
  const [ pendingAction, setPendingAction ] = useState(null)
  // A reopened code never brings its address back (the server returns only
  // `email_saved`, whether one is saved), so its saved state names none. It
  // still has to read as saved: "Save code" on a code that already has an
  // address ends in the server's 422.
  const [ savedTo, setSavedTo ] = useState(bound ? creatorEmail : (result?.email_saved ? "your email" : null))
  const [ copied, setCopied ] = useState(false)
  const [ logoError, setLogoError ] = useState(null)
  const [ upgradeOpen, setUpgradeOpen ] = useState(false)
  // One shot per code, and the intro presentation spends it. A prompt that
  // reappears on every download is a nag, and the design is explicit that it
  // fires at most once.
  const leaveShown = useRef(true)
  // Which pitch the save sheet makes. "intro" lands before the visitor has done
  // anything, so it can't talk about leaving; "leave" is the interception.
  const [ saveVariant, setSaveVariant ] = useState("intro")
  const drag = useRef(null)

  // The overlay owns the viewport while it's up, so the page behind it must not
  // scroll — otherwise a flick on the stage scrolls the marketing copy under a
  // fixed layer, which reads as the app breaking.
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => { document.body.style.overflow = prev }
  }, [])

  // Measured, because the QR size is derived from it. A ResizeObserver rather
  // than a resize listener so an on-screen keyboard or a URL bar collapsing —
  // neither of which fires a window resize on every mobile browser — still
  // re-sizes the code.
  const [ region, regionPx ] = useMeasuredHeight(0, (el) => el.clientHeight)

  const regionHeight = () => region.current?.clientHeight || window.innerHeight || 700

  // The chrome under the code is measured rather than assumed: the link pill's
  // height follows its 44px tap targets, and the "Saved to …" note appears
  // after a save. A constant would be wrong the moment either changed, and
  // wrong in the direction that clips the pill.
  const [ stageChrome, chromePx ] = useMeasuredHeight(58)
  const [ grabber, grabberPx ] = useMeasuredHeight(44)
  // Without the file hint, which only shows with the panel open. The floor is the
  // closed drawer's height, and if it moved with the hint, a drag near the peek
  // boundary would flip the mode, which moves the floor, which flips it back.
  const hint = useRef(null)
  const [ footer, footerPx ] = useMeasuredHeight(83, (el) => el.offsetHeight - (hint.current?.offsetHeight ?? 0))

  const floorFrac = regionPx ? (grabberPx + CLOSED_ROW_PX + footerPx) / regionPx : 0
  const rungs = rungsFor(floorFrac)
  const dragging = dragFrac !== null
  const shownFrac = dragging ? dragFrac : rungs[rung]
  const collapsed = shownFrac <= rungs[CLOSED_RUNG] + COLLAPSED_BAND
  // Past PEEK_MAX on a very short screen the peek rung still has no room for a
  // working panel, so it stays a peek.
  const peek = !collapsed && shownFrac <= Math.max(PEEK_MAX, rungs[PEEK_RUNG])

  function handlePointerDown(e) {
    e.currentTarget.setPointerCapture(e.pointerId)
    drag.current = { y: e.clientY, startFrac: shownFrac, frac: shownFrac, moved: false }
    setDragFrac(shownFrac)
  }

  function handlePointerMove(e) {
    if (!drag.current) return
    const delta = drag.current.y - e.clientY
    if (Math.abs(delta) > TAP_SLOP_PX) drag.current.moved = true
    drag.current.frac = Math.min(MAX_FRAC, Math.max(rungs[CLOSED_RUNG], drag.current.startFrac + delta / regionHeight()))
    setDragFrac(drag.current.frac)
  }

  function handlePointerUp() {
    if (!drag.current) return
    const { moved, frac } = drag.current
    drag.current = null
    setDragFrac(null)
    // A tap toggles the drawer closed <-> half — the "close the drawer" gesture
    // and its inverse. Handled here rather than in a separate onClick so the
    // two can't both fire and fight over the same frame.
    if (!moved) setRung(rung === CLOSED_RUNG ? HALF_RUNG : CLOSED_RUNG)
    else setRung(nearestRung(rungs, frac))
  }

  // Not a tap and not a choice of height: the browser took the pointer away, so
  // the sheet goes back to its rung.
  function handlePointerCancel() {
    drag.current = null
    setDragFrac(null)
  }

  // The grabber is a separator; a separator that only responds to a pointer is
  // unusable by keyboard, so arrows step through the rungs.
  function handleGrabberKey(e) {
    if (e.key === "ArrowUp")        { e.preventDefault(); setRung(Math.min(rungs.length - 1, rung + 1)) }
    else if (e.key === "ArrowDown") { e.preventDefault(); setRung(Math.max(CLOSED_RUNG, rung - 1)) }
    else if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setRung(rung === CLOSED_RUNG ? HALF_RUNG : CLOSED_RUNG) }
  }

  // Intercepts the two ways a visitor leaves with an unsaved code. The
  // intercepted action runs after they save or skip, never instead of it.
  const guard = useCallback((run) => {
    if (!savedTo && !leaveShown.current) {
      leaveShown.current = true
      setSaveVariant("leave")
      setPendingAction(() => run)
      setModal("leave")
      return
    }
    run()
  }, [ savedTo ])

  const shareUrl = result?.redirect_url || result?.short_url
  const shortDisplay = shareUrl ? shareUrl.replace(/^https?:\/\//, "") : ""
  const hasLogo = Boolean(design.logo || (result?.logo_url && !design.removeLogo))
  const summary = summarize(design, hasLogo)

  async function handleCopy() {
    if (!shareUrl) return
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch { setCopied(false) }
  }

  function handleDownload(format) {
    guard(() => {
      const href = format === "svg" ? result.image_url : pngFor(result.image_url)
      if (!href) return
      const a = document.createElement("a")
      a.href = asDownload(href)
      a.download = `tango-qr-${result.token}.${format}`
      a.rel = "noopener"
      document.body.appendChild(a)
      a.click()
      a.remove()
    })
  }

  function closeLeaveSheet() {
    const run = pendingAction
    setModal(null)
    setPendingAction(null)
    if (run) run()
  }

  function handleSaved(address) {
    setSavedTo(address)
    // So the builder knows this code has an address now, if it is reopened.
    onEmailSaved?.()
    setModal(null)
    setPendingAction(null)
    echo("Sent — check your inbox")
    // The action they were on their way to do still happens; saving shouldn't
    // cost them the download they asked for.
    if (pendingAction) pendingAction()
  }

  const goUpgrade = () => (onUpgrade ? onUpgrade() : setUpgradeOpen(true))

  // Every setter echoes what it changed, in the same words the control uses.
  const panelProps = {
    category,
    design,
    setDesign,
    existingLogoUrl: result?.logo_url || null,
    locked: !isPaid,
    error: logoError,
    setError: setLogoError,
    onColors: (p) => { setDesign((d) => ({ ...d, foregroundColor: p.foreground, backgroundColor: p.background })); echo(`Colors · ${p.name}`) },
    onForeground: (hex) => { setDesign((d) => ({ ...d, foregroundColor: hex })); echo(`Foreground ${hex.toUpperCase()}`) },
    onBackground: (hex) => { setDesign((d) => ({ ...d, backgroundColor: hex })); echo(`Background ${hex.toUpperCase()}`) },
    onFrame: (f) => { setDesign((d) => ({ ...d, frameStyle: f.value })); echo(`Frame · ${f.label}`) },
    onModule: (o) => { setDesign((d) => ({ ...d, moduleShape: o.value })); echo(`Dots · ${o.label}`) },
    onEye: (o) => { setDesign((d) => ({ ...d, eyeShape: o.value })); echo(`Corners · ${o.label}`) },
    onLogo: (file) => { setDesign((d) => ({ ...d, logo: file, removeLogo: false })); echo("Logo added") },
    onRemoveLogo: () => { setDesign((d) => ({ ...d, logo: null, removeLogo: true })); echo("Logo removed") },
    onUpgrade: () => setModal("pro")
  }

  const previewDesign = { ...design, token: result.token }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-[color:var(--bg-surface)] text-[color:var(--text-primary)]">
      {/* Change echo — names what just happened, because on a phone the change
          lands behind the user's thumb on a code small enough to hide it.

          Centring is the flex row's job; the chip only fades and rises. Doing
          both on one element put the horizontal centring inside the keyframe,
          and Tailwind v4 centres with the `translate` PROPERTY while the
          keyframe used `transform` — so for 160ms the two stacked and the chip
          flew in from half its own width to the left before snapping to
          centre. Two properties that both move an element must not be split
          across a utility and a keyframe.

          It floats over the HEADER, not over the stage. The prototype had an
          iOS status-bar strip to sit in and we don't, and the stage is the
          code: a chip that names the change you just made must not cover the
          thing that changed. The header is chrome, and briefly overlapping a
          wordmark nobody is reading for 1500ms is the cheaper cost.

          The live region stays mounted and only its contents change. A
          role="status" that appears together with its text is announced
          unreliably — there was no region to observe when the text arrived. */}
      <div className="pointer-events-none absolute inset-x-0 top-3 z-30 flex justify-center px-4" role="status">
        {echoMessage && (
          <span className="inline-flex max-w-full items-center gap-2 truncate rounded-full bg-coal px-3.5 py-2 text-xs font-semibold text-voltage-light shadow-md motion-safe:animate-[echoIn_160ms_cubic-bezier(0.4,0,0.2,1)]">
            <Icon name="sparkles" size={13} /> {echoMessage}
          </span>
        )}
      </div>

      <div
        className="flex flex-shrink-0 items-center justify-between border-b border-[color:var(--border-default)] px-4 py-2.5"
        style={{ paddingTop: "calc(0.625rem + env(safe-area-inset-top))" }}
      >
        <span className="font-display text-lg font-extrabold tracking-tight">
          <span className="text-[color:var(--text-primary)]">Tango</span><span className="text-voltage">QR</span>
        </span>
        {/* No close control. This shell IS the hero on a phone, so "closing" it
            would drop the visitor back onto the very stacked layout this
            replaces — a regression dressed as an escape hatch. The two ways out
            are the ones that keep meaning: collapse the drawer to see the code
            at full size, or start a new code (which returns to the idle hero
            and the scrolling page). */}
        <button
          type="button"
          onClick={() => guard(onReset)}
          className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full border border-[color:var(--border-default)] px-3.5 text-sm font-semibold text-[color:var(--text-secondary)] transition hover:border-voltage focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2"
        >
          <Icon name="plus" size={15} /> New code
        </button>
      </div>

      <div ref={region} className="relative min-h-0 flex-1">
        {/* Stage — everything above the sheet. */}
        <div
          className={
            "absolute inset-x-0 top-0 flex flex-col items-center gap-2.5 px-4 pt-3.5 " +
            "bg-[color:var(--bg-secondary)] " +
            (dragging ? "" : "transition-[bottom] duration-[240ms] ease-[cubic-bezier(0.4,0,0.2,1)]")
          }
          style={{ bottom: `${shownFrac * 100}%` }}
        >
          {/* Sized, not transform-scaled. `transform: scale()` shrinks what is
              painted but not the layout box, so a scaled-down QR still reserved
              its full 188px and pushed the link pill down under the sheet —
              visibly clipping it at 360px wide. Setting the width lets the
              square box drive its own height, so everything below it moves up
              with it. Deliberately no overflow-hidden on the stage: at the
              tallest snap the code is meant to slide *behind* the sheet (which
              paints above it), not be sheared off mid-element. */}
          <div
            className="w-full transition-[max-width] duration-200 ease-[cubic-bezier(0.4,0,0.2,1)]"
            style={{ maxWidth: `${qrSizePx(regionPx * (1 - shownFrac), chromePx, collapsed)}px` }}
          >
            {/* fallbackSvg seeds the preview with the SVG the create response
                already returned, so the stage opens showing the actual code
                instead of "Rendering…" for a debounce plus a round trip. On the
                stage the code IS the screen, so that placeholder is the first
                thing a visitor would see. (HeroResult has the same gap on
                desktop, where it sits in a side rail and reads as a detail —
                worth the same one-line fix, but desktop is out of scope here.) */}
            <LivePreview design={previewDesign} fallbackSvg={result.svg} maxWidth="max-w-full" />
          </div>

          <div ref={stageChrome} className="flex w-full flex-col items-center gap-2.5">
          <div className="flex max-w-full items-center gap-1.5 rounded-full border border-[color:var(--border-default)] bg-[color:var(--bg-surface)] py-1.5 pl-3.5 pr-1.5">
            <span className="truncate font-mono text-sm">{shortDisplay}</span>
            {/* Copy only. "Start a new code" moved to the header, where it
                reads as navigation rather than as an action on this link. */}
            <button type="button" onClick={handleCopy} className={ICON_BTN} aria-label="Copy link">
              <Icon name={copied ? "check" : "copy"} />
            </button>
          </div>

          {/* A hosted contact page (#332): the same honest line as HeroResult. */}
          {result.contact_page && (
            <p className="m-0 text-center text-xs text-[color:var(--text-secondary)]">
              A scan opens your page. One more tap saves your contact.{" "}
              <a href={result.destination_url} target="_blank" rel="noopener noreferrer" className="font-semibold text-voltage-deep underline underline-offset-2 dark:text-voltage-light">
                Open your page
              </a>
            </p>
          )}

          {savedTo ? (
            <p className="m-0 flex items-center gap-1.5 text-xs font-semibold text-voltage-deep dark:text-voltage-light">
              <Icon name="check" size={13} /> Saved to {savedTo}
            </p>
          ) : creatorEmail ? (
            // Refused by a ceiling. The code is live and still owned by this
            // browser via the creation-session cookie, so the honest next step
            // is signing in — not a second attempt that would trip the same
            // ceiling.
            <p className="m-0 text-center text-xs text-[color:var(--text-secondary)]">
              Couldn't save <span className="font-mono">{creatorEmail}</span>.{" "}
              <a href="/login?return_to=/app" className="font-semibold text-voltage-deep underline underline-offset-2 dark:text-voltage-light">
                Sign in to keep it
              </a>
            </p>
          ) : null}

          {restyleError && (
            <p className="m-0 text-center text-xs text-[color:var(--color-fail)]">{restyleError}</p>
          )}
          </div>
        </div>

        {/* Sheet. */}
        <div
          className={
            "absolute inset-x-0 bottom-0 z-10 flex touch-none flex-col rounded-t-[22px] " +
            "border-t border-[color:var(--border-default)] bg-[color:var(--bg-surface)] " +
            "shadow-[0_-12px_32px_rgba(17,17,17,0.12)] " +
            (dragging ? "" : "transition-[height] duration-[240ms] ease-[cubic-bezier(0.4,0,0.2,1)]")
          }
          style={{ height: `${shownFrac * 100}%` }}
        >
          <div
            ref={grabber}
            role="separator"
            tabIndex={0}
            aria-label="Drag to resize settings"
            aria-orientation="horizontal"
            aria-valuenow={Math.round(shownFrac * 100)}
            aria-valuemin={Math.round(rungs[CLOSED_RUNG] * 100)}
            aria-valuemax={Math.round(MAX_FRAC * 100)}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerCancel}
            onKeyDown={handleGrabberKey}
            className="flex h-11 flex-shrink-0 cursor-grab items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-inset"
          >
            <span aria-hidden="true" className="block h-1 w-[38px] rounded-sm bg-[color:var(--border-default)]" />
          </div>

          {collapsed ? (
            // Closed drawer: the code gets the screen. One labelled row so the
            // way back is named rather than left to a discovered gesture on a
            // 38px bar.
            <button
              type="button"
              onClick={() => setRung(HALF_RUNG)}
              className="flex min-h-[44px] w-full flex-shrink-0 items-center justify-center gap-1.5 px-4 text-sm font-semibold text-[color:var(--text-secondary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-inset"
            >
              Style your code
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="18 15 12 9 6 15" />
              </svg>
            </button>
          ) : peek ? (
            <div className="min-h-0 flex-1 overflow-hidden pb-1">
              <p className="mx-4 mb-2 text-sm font-bold">Style your code</p>
              <div className="grid grid-cols-2 gap-2 px-4 pb-1.5">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => { setCategory(c.id); setRung(HALF_RUNG) }}
                    className="inline-flex min-h-[44px] min-w-0 items-center justify-between gap-2 rounded-full border border-[color:var(--border-default)] px-3 text-sm font-semibold text-[color:var(--text-secondary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2"
                  >
                    <span className="truncate">{c.label}</span>
                    <span className="flex-shrink-0 border-l border-[color:var(--border-default)] pl-2 text-xs font-semibold">
                      {summary[c.id]}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div role="tablist" aria-label="Settings" className="flex flex-shrink-0 gap-1.5 overflow-x-auto px-3.5 pb-2.5 pt-1.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {CATEGORIES.map((c) => {
                  const on = category === c.id
                  return (
                    <button
                      key={c.id}
                      type="button"
                      role="tab"
                      aria-selected={on}
                      aria-pressed={on}
                      onClick={() => setCategory(c.id)}
                      className={
                        "inline-flex min-h-[44px] flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-sm font-semibold " +
                        "focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2 " +
                        (on
                          ? "border-coal bg-coal text-voltage-light"
                          : "border-[color:var(--border-default)] text-[color:var(--text-secondary)]")
                      }
                    >
                      {c.label}
                      {c.id === "shape" && !isPaid && (
                        <span className="text-plasma-light"><Icon name="lock" size={12} /></span>
                      )}
                    </button>
                  )
                })}
              </div>
              <div className="min-h-0 flex-1 overflow-auto overscroll-contain px-4 pb-4 pt-1">
                <MobileSettingPanels {...panelProps} />
              </div>
            </>
          )}

          <div
            ref={footer}
            className="flex-shrink-0 border-t border-[color:var(--border-default)] px-4 pt-2.5"
            style={{ paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}
          >
            {/* Both files, as on desktop: a sign shop asks for a vector, and a
                phone is where some people have to answer it (TQ-02). */}
            <div className="grid grid-cols-[1fr_1fr_1.5fr] gap-2.5">
              <button
                type="button"
                onClick={() => handleDownload("png")}
                aria-label="Download PNG"
                className={DOWNLOAD_BTN}
              >
                <Icon name="download" /> PNG
              </button>
              <button
                type="button"
                onClick={() => handleDownload("svg")}
                aria-label="Download SVG"
                className={DOWNLOAD_BTN}
              >
                <Icon name="download" /> SVG
              </button>
              {/* Inert once the address is captured. Re-opening a sheet titled
                  "keep this code editable" for a code that is already kept asks
                  for something we have. */}
              <button
                type="button"
                disabled={Boolean(savedTo)}
                onClick={() => { leaveShown.current = true; setSaveVariant("leave"); setModal("leave") }}
                className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-md bg-voltage text-base font-semibold text-coal shadow-voltage transition hover:bg-voltage-dark disabled:cursor-default disabled:opacity-60 disabled:shadow-none focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2"
              >
                <Icon name={savedTo ? "check" : "mail"} /> {savedTo ? "Saved" : "Save code"}
              </button>
            </div>
            {/* Only with the panel open: the closed and peek rungs have no
                height to spare, and this line would push their controls off
                the sheet. */}
            {!collapsed && !peek && (
              <p ref={hint} className="m-0 pt-2 text-center text-xs text-[color:var(--text-secondary)]">
                {WHICH_FILE_HINT}
              </p>
            )}
          </div>
        </div>
      </div>

      <MobileProSheet
        open={modal === "pro"}
        onClose={() => setModal(null)}
        onUpgrade={() => { setModal(null); goUpgrade() }}
      />
      <MobileSaveSheet
        open={modal === "leave"}
        token={result.token}
        onClose={closeLeaveSheet}
        onSaved={handleSaved}
        variant={saveVariant}
        skipLabel={pendingAction ? "No thanks, just download" : "Not now"}
      />
      {/* Always "shapes": the only way here is the Pro sheet, and only a locked
          shape raises that. A second entry point (the wordmark, say) must pass
          its own reason, or its click gets a headline about shapes (#268). */}
      <PricingModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} claimToken={result.token} reason="shapes" />
    </div>
  )
}
